import { dequal } from 'dequal'
import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { AppState } from '@redux/store'
import { workflowSlice } from './workflowSlice'
import {
  ImagePreviewParam,
  PrescriptionFileSelection,
  PrescriptionHelpModal,
  UploadMode,
} from '@typesApp/PrescriptionFile'

export interface PrescriptionFileState {
  selected: PrescriptionFileSelection
  uploadedFiles: string[] /* file names */
  uploadMode: UploadMode
  imagePreview: ImagePreviewParam | undefined
  helpModal?: PrescriptionHelpModal
}

const selectionInitialState = {
  index: -1,
}

const initialState = {
  selected: selectionInitialState,
  uploadedFiles: [],
  uploadMode: UploadMode.DEFAULT,
  imagePreview: undefined,
} as PrescriptionFileState

const REDUCER_PATH = 'prescriptionFile'

export const prescriptionFileSlice = createSlice({
  name: REDUCER_PATH,
  initialState: initialState,
  reducers: {
    setUploadedFiles(state, action: PayloadAction<string[]>) {
      state.uploadedFiles = action.payload
    },
    clearUploadedFiles(state) {
      state.uploadedFiles = []
    },
    selectPrescriptionFile(state, action: PayloadAction<PrescriptionFileSelection>) {
      state.selected = action.payload
    },
    deselectPrescriptionFile(state) {
      state.selected = selectionInitialState
    },
    setPrescriptionImageParams(state, action: PayloadAction<ImagePreviewParam>) {
      // update image preview params if image preview is open and selected
      // prescription file changes
      if (
        state.imagePreview &&
        action.payload.name &&
        action.payload.url &&
        state.selected.name === action.payload.name
      ) {
        state.imagePreview = action.payload
      }
    },
    setPrescriptionUploadMode(state, action: PayloadAction<UploadMode>) {
      state.uploadMode = action.payload
    },
    showPrescriptionImagePreview(state, action: PayloadAction<ImagePreviewParam>) {
      state.imagePreview = dequal(state.imagePreview, action.payload) ? undefined : action.payload
    },
    closePrescriptionImagePreview(state) {
      state.imagePreview = undefined
    },
    closePrescriptionImagePreviewByName(state, action: PayloadAction<string>) {
      const isNameMatch = state.imagePreview?.name && state.imagePreview?.name === action.payload
      if (isNameMatch) {
        state.imagePreview = undefined
      }
    },
    openPrescriptionHelpModal(state, action: PayloadAction<PrescriptionHelpModal>) {
      state.helpModal = action.payload
    },
    closePrescriptionHelpModal(state) {
      state.helpModal = undefined
    },
    resetPrescriptionFileInitialState() {
      return initialState
    },
  },
  extraReducers: builder => {
    // close preview modal when navigating to different step
    builder.addCase(workflowSlice.actions.setProgressStep, state => {
      state.imagePreview = undefined
    })
  },
})

export const {
  setUploadedFiles,
  clearUploadedFiles,
  selectPrescriptionFile,
  deselectPrescriptionFile,
  setPrescriptionUploadMode,
  setPrescriptionImageParams,
  showPrescriptionImagePreview,
  closePrescriptionImagePreview,
  closePrescriptionImagePreviewByName,
  openPrescriptionHelpModal,
  closePrescriptionHelpModal,
  resetPrescriptionFileInitialState,
} = prescriptionFileSlice.actions

export const prescriptionFileUploadedSelector = (state: AppState): string[] => state.prescriptionFile.uploadedFiles
export const prescriptionFileSelectionSelector = (state: AppState): PrescriptionFileSelection =>
  state.prescriptionFile.selected
export const prescriptionFileUploadModeSelector = (state: AppState): UploadMode => state.prescriptionFile.uploadMode
export const prescriptionImagePreviewSelector = (state: AppState): ImagePreviewParam | undefined =>
  state.prescriptionFile.imagePreview
export const prescriptionHelpModalSelector = (state: AppState): PrescriptionHelpModal | undefined =>
  state.prescriptionFile.helpModal
